import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Package, 
  Star, 
  Copy, 
  Eye, 
  Edit3, 
  Trash2, 
  Plus, 
  Search, 
  Filter,
  Loader2,
  CheckCircle2,
  Sparkles,
  Building2,
  TrendingUp,
  Users,
  Download,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { CreateSOWTemplateDialog } from './CreateSOWTemplateDialog';

interface SOWTemplateLibraryProps {
  accessToken: string;
  firmId: string;
  onApplyTemplate?: (template: SOWTemplate) => void;
}

interface SOWTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  industry: string;
  projectType: string;
  budgetRange: string;
  estimatedHours: number;
  
  // Template Content
  executiveSummary: string;
  scope: string;
  deliverables: string[];
  assumptions: string[];
  exclusions: string[];
  phases: Array<{
    name: string;
    duration: string;
    description: string;
  }>;
  risks: string[];
  successCriteria: string[];
  paymentTerms: string;
  
  // Engagement Type Mapping
  engagementTypeId?: string;
  engagementTypeName?: string;
  
  // Metadata
  firmId: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  isBaseline?: boolean; // Pathkit-provided baseline template
  usageCount: number;
  rating: number;
  tags: string[];
}

interface EngagementType {
  id: string;
  name: string;
  description: string;
  category: string;
}

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'migration', label: 'Migration Projects' },
  { value: 'implementation', label: 'New Implementations' },
  { value: 'optimization', label: 'Optimization & Tuning' },
  { value: 'upgrade', label: 'Upgrades & Modernization' },
  { value: 'integration', label: 'System Integration' },
  { value: 'training', label: 'Training & Enablement' },
  { value: 'assessment', label: 'Health Checks & Assessments' },
];

const INDUSTRIES = [
  { value: 'all', label: 'All Industries' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'technology', label: 'Technology / SaaS' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
];

export function SOWTemplateLibrary({ accessToken, firmId, onApplyTemplate }: SOWTemplateLibraryProps) {
  const [templates, setTemplates] = useState<SOWTemplate[]>([]);
  const [engagementTypes, setEngagementTypes] = useState<EngagementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [engagementTypeFilter, setEngagementTypeFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState<'mine' | 'all'>('mine'); // BD Rep default
  const [selectedTemplate, setSelectedTemplate] = useState<SOWTemplate | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<SOWTemplate | null>(null);
  const [editTemplate, setEditTemplate] = useState<SOWTemplate | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  useEffect(() => {
    loadTemplates();
    loadEngagementTypes();
    loadCurrentUser();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadEngagementTypes = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEngagementTypes(data.engagementTypes || []);
      } else {
        toast.error('Failed to load engagement types');
      }
    } catch (error) {
      console.error('Error loading engagement types:', error);
      toast.error('Failed to load engagement types');
    }
  };

  const loadCurrentUser = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/current-user`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentUserId(data.userId || '');
      } else {
        toast.error('Failed to load current user');
      }
    } catch (error) {
      console.error('Error loading current user:', error);
      toast.error('Failed to load current user');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Template deleted');
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
        loadTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleApplyTemplate = (template: SOWTemplate) => {
    if (onApplyTemplate) {
      onApplyTemplate(template);
      toast.success('Template applied. Customize as needed.');
    }
  };

  const handleCloneTemplate = async (template: SOWTemplate) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates/${template.id}/clone`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        toast.success('Template cloned');
        loadTemplates();
      } else {
        toast.error('Failed to clone template');
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      toast.error('Failed to clone template');
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (
        !template.name.toLowerCase().includes(searchLower) &&
        !template.description.toLowerCase().includes(searchLower) &&
        !template.tags.some(tag => tag.toLowerCase().includes(searchLower))
      ) {
        return false;
      }
    }

    // Category filter
    if (categoryFilter !== 'all' && template.category !== categoryFilter) {
      return false;
    }

    // Industry filter
    if (industryFilter !== 'all' && template.industry !== industryFilter) {
      return false;
    }

    // Engagement Type filter
    if (engagementTypeFilter !== 'all' && template.engagementTypeId !== engagementTypeFilter) {
      return false;
    }

    // Owner filter
    if (ownerFilter === 'mine' && template.createdBy !== currentUserId) {
      return false;
    }

    return true;
  });

  // Calculate stats
  const totalTemplates = templates.length;
  const myTemplates = templates.filter(t => t.createdBy === currentUserId).length;
  const baselineTemplates = templates.filter(t => t.isBaseline).length;
  const customTemplates = totalTemplates - baselineTemplates;
  const mostUsedTemplate = templates.reduce((prev, current) => 
    (current.usageCount > prev.usageCount) ? current : prev, 
    templates[0] || { usageCount: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl">
            <Package className="h-6 w-6 text-purple-500" />
            SOW Template Library
          </h2>
          <p className="text-muted-foreground mt-1">
            Save time by reusing your best SOW patterns
          </p>
        </div>
        <CreateSOWTemplateDialog accessToken={accessToken} firmId={firmId} onTemplateCreated={loadTemplates} />
      </div>

      {/* Quick Stats */}
      {!loading && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Templates</p>
                  <p className="text-2xl">{totalTemplates}</p>
                </div>
                <Package className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">My Templates</p>
                  <p className="text-2xl">{myTemplates}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pathkit Templates</p>
                  <p className="text-2xl">{baselineTemplates}</p>
                </div>
                <Building2 className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Most Used</p>
                  <p className="text-lg line-clamp-1">{mostUsedTemplate?.name || 'N/A'}</p>
                  <p className="text-xs text-muted-foreground">{mostUsedTemplate?.usageCount || 0} uses</p>
                </div>
                <TrendingUp className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search templates by name, description, or tags..."
                  className="pl-10"
                />
              </div>
            </div>

            <div className="w-48">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="w-48">
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {INDUSTRIES.map(ind => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
            </div>

            <div className="w-48">
              <select
                value={engagementTypeFilter}
                onChange={(e) => setEngagementTypeFilter(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="all">All Engagement Types</option>
                {engagementTypes.map(et => (
                  <option key={et.id} value={et.id}>{et.name}</option>
                ))}
              </select>
            </div>

            <div className="w-48">
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value as 'mine' | 'all')}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="mine">My Templates</option>
                <option value="all">All Templates</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-muted-foreground">
              {searchTerm || categoryFilter !== 'all' || industryFilter !== 'all' || engagementTypeFilter !== 'all' || ownerFilter !== 'all'
                ? 'No templates found matching your filters'
                : 'No templates yet. Create your first template to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {template.description}
                    </CardDescription>
                  </div>
                  {template.rating > 0 && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      {template.rating.toFixed(1)}
                    </Badge>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {/* Baseline vs Custom Badge */}
                  {template.isBaseline ? (
                    <Badge className="text-xs bg-green-100 text-green-700 border-green-200">
                      <Building2 className="h-3 w-3 mr-1" />
                      Pathkit Template
                    </Badge>
                  ) : (
                    <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Custom
                    </Badge>
                  )}
                  
                  <Badge variant="outline" className="text-xs">
                    {CATEGORIES.find(c => c.value === template.category)?.label || template.category}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {INDUSTRIES.find(i => i.value === template.industry)?.label || template.industry}
                  </Badge>
                  {template.engagementTypeName && (
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                      <Briefcase className="h-3 w-3 mr-1" />
                      {template.engagementTypeName}
                    </Badge>
                  )}
                  {template.tags.slice(0, 1).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <span className="text-muted-foreground">{template.estimatedHours}h</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-muted-foreground">{template.usageCount} uses</span>
                  </div>
                </div>

                {/* Key Deliverables Preview */}
                <div>
                  <p className="text-sm mb-1">Includes:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {template.deliverables.slice(0, 3).map((deliverable, index) => (
                      <li key={index} className="flex items-start gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">{deliverable}</span>
                      </li>
                    ))}
                    {template.deliverables.length > 3 && (
                      <li className="text-xs text-gray-500">+{template.deliverables.length - 3} more</li>
                    )}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleApplyTemplate(template)}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Use Template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedTemplate(template);
                      setPreviewDialogOpen(true);
                    }}
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  {/* Edit button - only show for own templates */}
                  {template.createdBy === currentUserId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditTemplate(template)}
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCloneTemplate(template)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTemplateToDelete(template);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-6 py-4">
              {/* Executive Summary */}
              <div>
                <h3 className="font-medium mb-2">Executive Summary</h3>
                <p className="text-sm text-muted-foreground">{selectedTemplate.executiveSummary}</p>
              </div>

              {/* Scope */}
              <div>
                <h3 className="font-medium mb-2">Scope</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedTemplate.scope}</p>
              </div>

              {/* Deliverables */}
              <div>
                <h3 className="font-medium mb-2">Deliverables ({selectedTemplate.deliverables.length})</h3>
                <ul className="space-y-1">
                  {selectedTemplate.deliverables.map((deliverable, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{deliverable}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phases */}
              {selectedTemplate.phases.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Project Phases</h3>
                  <div className="space-y-2">
                    {selectedTemplate.phases.map((phase, index) => (
                      <div key={index} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{phase.name}</span>
                          <Badge variant="outline">{phase.duration}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{phase.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Estimated Budget */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-900">Estimated Hours</p>
                    <p className="text-2xl text-blue-600">{selectedTemplate.estimatedHours}h</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-900">Budget Range</p>
                    <p className="text-lg text-blue-600">{selectedTemplate.budgetRange}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              if (selectedTemplate) {
                handleApplyTemplate(selectedTemplate);
                setPreviewDialogOpen(false);
              }
            }}>
              <Sparkles className="h-4 w-4 mr-2" />
              Use This Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (templateToDelete) {
                  handleDeleteTemplate(templateToDelete.id);
                }
              }}
            >
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      {editTemplate && (
        <CreateSOWTemplateDialog
          accessToken={accessToken}
          firmId={firmId}
          onTemplateCreated={() => {
            loadTemplates();
            setEditTemplate(null);
          }}
          editTemplate={editTemplate}
          onClose={() => setEditTemplate(null)}
        />
      )}
    </div>
  );
}